import {useState, useEffect} from 'react';
import {FormFA, ProjectFA} from "../interfaces/global-interfaces";
import {createForm, createProject, deleteForm, deleteProject, patchProject} from "../services/apiService";
import {useProject} from "../context/ProjectContext";
import {ConfirmModal} from "../components/ConfirmModal";
import {UpsertProjectForm} from "../components/UpsertProjectForm";
import {AlertTriangle, ArrowLeft, Check, FileText, Plus, Settings2, Trash2, Webhook, Globe} from "lucide-react";
import {Card} from "./ui/Card";
import {Button} from "./ui/Button";
import {Input, Label, Textarea} from "./ui/Input";
import {EmptyState} from "./ui/EmptyState";
import {Tabs} from "./ui/Tabs";
import {useToast} from "./ui/Toast";
import {staggerDelay} from "../utils/animation";

export const UpsertProject = ({
                                  project,
                                  forms,
                                  onUpdate,
                                  onDeleteForm,
                                  onDeleteProject
                              }: {
    project?: ProjectFA;
    forms?: FormFA[];
    onUpdate: (updated: ProjectFA, forms?: FormFA[]) => void;
    onDeleteForm?: (formId: string) => void;
    onDeleteProject?: () => void;
}) => {
    const isEditMode = !!project;
    const [projectName, setProjectName] = useState(project?.name ?? '');
    const [projectDescription, setProjectDescription] = useState(project?.description ?? '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<'config' | 'forms'>('config');
    const [showFormEditor, setShowFormEditor] = useState(false);
    const [formList, setFormList] = useState<FormFA[]>(forms ?? []);
    const {projects, setProjects, setSelectedProjectId} = useProject();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ type: 'project' | 'form'; id?: string; label: string } | null>(null);
    const {showToast} = useToast();

    const handleSubmit = async () => {
        if (projectName.trim().length < 3) return;

        setIsSubmitting(true);
        const payload = {
            name: projectName,
            description: projectDescription
        };

        try {
            let updatedProject: ProjectFA;
            if (isEditMode && project?._id) {
                updatedProject = await patchProject(project._id, payload);
                setProjects(
                    projects.map(p => p._id === updatedProject._id ? updatedProject : p)
                );
            } else {
                updatedProject = await createProject(payload);
                setProjects([...projects, updatedProject]);
                setSelectedProjectId(updatedProject._id!);
            }
            showToast(isEditMode ? "Project updated." : "Project created.", "success");
            onUpdate(updatedProject, formList);
        } catch (error) {
            console.error("Error creating/updating project:", error);
            showToast("Couldn't save this project. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateForm = async (formData: FormFA) => {
        if (!project?._id) return;
        try {
            const newForm = await createForm(project._id, formData);
            const updatedForms = [...formList, newForm];
            setFormList(updatedForms);
            setShowFormEditor(false);
            showToast("Form created.", "success");
            onUpdate(project!, updatedForms);
        } catch (err) {
            console.error('Error creating form:', err);
            showToast("Couldn't create this form. Please try again.", "error");
        }
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            if (itemToDelete.type === 'project' && project?._id) {
                await deleteProject(project._id);
                setProjects(projects.filter(p => p._id !== project._id));
                showToast(`"${itemToDelete.label}" was deleted.`, "success");
                onDeleteProject?.();
            } else if (itemToDelete.type === 'form' && itemToDelete.id && project?._id) {
                await deleteForm(project._id, itemToDelete.id);
                const updatedForms = formList.filter(f => f._id !== itemToDelete.id);
                setFormList(updatedForms);
                showToast(`"${itemToDelete.label}" was deleted.`, "success");
                onDeleteForm?.(itemToDelete.id);
            }
        } catch (err) {
            console.error('Error deleting:', err);
            showToast(`Couldn't delete this ${itemToDelete.type}. Please try again.`, "error");
        } finally {
            setIsConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const confirmDelete = (type: 'project' | 'form', label: string, id?: string) => {
        setItemToDelete({type, id, label});
        setIsConfirmOpen(true);
    };

    useEffect(() => {
        if (project) {
            setProjectName(project.name ?? '');
            setProjectDescription(project.description ?? '');
        }
    }, [project]);

    useEffect(() => {
        setFormList(forms ?? []);
    }, [forms]);

    return (
        <>
            <ConfirmModal
                isOpen={isConfirmOpen}
                title="Confirm Deletion"
                message={`Are you sure you want to delete ${itemToDelete?.type === 'project' ? 'the project' : 'the form'} "${itemToDelete?.label}"? This can't be undone.`}
                showDeleteButton={true}
                onConfirm={handleConfirmDelete}
                onCancel={() => setIsConfirmOpen(false)}
            />

            <div className="flex flex-col gap-6 shrink-0 w-full mx-auto">
                {showFormEditor ? (
                    <div className="w-full h-full flex flex-col">
                        <div className="flex gap-3 items-center mb-6 shrink-0">
                            <button
                                onClick={() => setShowFormEditor(false)}
                                className="size-9 flex items-center justify-center rounded-full text-ink-muted hover:text-primary hover:bg-surface-hover transition-colors"
                            >
                                <ArrowLeft size={18}/>
                            </button>
                            <h2 className="text-xl font-display font-bold text-ink">New Form</h2>
                        </div>
                        <UpsertProjectForm
                            onSubmit={handleCreateForm}
                            initialData={{project_id: project?._id!}}
                        />
                    </div>
                ) : (<>

                    <Tabs
                        tabs={[
                            {key: 'config', label: 'Configuration', icon: Settings2},
                            ...(isEditMode ? [{key: 'forms', label: 'Forms', icon: FileText}] : []),
                        ]}
                        active={activeTab}
                        onChange={(key) => setActiveTab(key as 'config' | 'forms')}
                    />

                    {/* Tab Content */}
                    {activeTab === 'config' && (
                        <Card className="space-y-5 animate-fade-in animate-duration-200">
                            <div>
                                <Label>Project Name</Label>
                                <Input
                                    type="text"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                />
                            </div>

                            <div>
                                <Label>Project Description</Label>
                                <Textarea
                                    value={projectDescription}
                                    onChange={(e) => setProjectDescription(e.target.value)}
                                />
                            </div>

                            <Button
                                icon={Check}
                                onClick={handleSubmit}
                                disabled={projectName.trim().length < 3 || isSubmitting}
                                loading={isSubmitting}
                            >
                                {isEditMode ? 'Update' : 'Create'}
                            </Button>

                            {isEditMode && (
                                <div className="mt-4 pt-5 border-t border-border">
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle size={15} className="text-error"/>
                                        <h3 className="text-sm font-semibold text-error">Danger Zone</h3>
                                    </div>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        icon={Trash2}
                                        onClick={() => confirmDelete('project', project!.name)}
                                    >
                                        Delete Project
                                    </Button>
                                </div>
                            )}
                        </Card>
                    )}

                    {activeTab === 'forms' && (
                        <div className="space-y-3 animate-fade-in animate-duration-200">
                            <Button size="sm" icon={Plus} onClick={() => setShowFormEditor(true)}>
                                New Form
                            </Button>
                            {formList && formList.length > 0 ? (
                                formList.map((form, i) => (
                                    <Card key={form._id} padding="sm" className="animate-fade-in-up" style={staggerDelay(i)}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <FileText size={15} className="text-ink-muted shrink-0"/>
                                            <p className="text-sm font-semibold text-ink">{form.name}</p>
                                        </div>

                                        {(form.webhooks?.length > 0 || form.accepted_domains?.length > 0) ? (
                                            <div className="rounded-xl border border-primary/25 bg-primary-tint p-4 space-y-3">
                                                {form.webhooks?.length > 0 && (
                                                    <div>
                                                        <div className="flex items-center gap-1.5">
                                                            <Webhook size={14} className="text-ink-muted"/>
                                                            <p className="text-xs font-semibold text-ink-muted">Webhooks</p>
                                                        </div>
                                                        <ul className="text-sm italic text-ink mt-1 space-y-0.5">
                                                            {form.webhooks.map((domain: string, idx: number) => (
                                                                <li key={idx}>{domain}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {form.accepted_domains?.length > 0 && (
                                                    <div>
                                                        <div className="flex items-center gap-1.5">
                                                            <Globe size={14} className="text-ink-muted"/>
                                                            <p className="text-xs font-semibold text-ink-muted">
                                                                Accepted domains
                                                            </p>
                                                        </div>
                                                        <ul className="text-sm italic text-ink mt-1 space-y-0.5">
                                                            {form.accepted_domains.map((domain: string, idx: number) => (
                                                                <li key={idx}>{domain}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="italic text-sm text-ink-faint">
                                                No data available for this form.
                                            </p>
                                        )}

                                        <div className="flex justify-end mt-3">
                                            <Button
                                                variant="danger-ghost"
                                                size="sm"
                                                icon={Trash2}
                                                onClick={() => confirmDelete('form', form.name, form._id)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </Card>
                                ))
                            ) : (
                                <Card>
                                    <EmptyState icon={FileText} title="There are no associated forms."/>
                                </Card>
                            )}
                        </div>
                    )}
                </>)}
            </div>
        </>
    );
};
